'use client';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { Button } from '@/components/ui/button';
import { Shield, Upload, Phone } from 'lucide-react';

import { useEffect, useState } from 'react';

import { useUser } from '@clerk/nextjs';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { DialogDescription } from '@radix-ui/react-dialog';

// Time in minutes before showing the popup again after dismissal
const expirationTimeInMinutes = 15;

export default function UserVerficationPopup() {
  const { user } = useUser();
  const pathname = usePathname();

  const [showDialog, setShowDialog] = useState(false);

  const closeDialog = (openDialog = false) => {
    if (openDialog) {
      setShowDialog(true);
      return;
    }

    setShowDialog(false);
    localStorage.setItem('verificationPopupClosedAt', Date.now().toString());
  };

  useEffect(() => {
    // Don't do anything if no user or on verification pages
    if (
      !user ||
      pathname === '/user/verification' ||
      pathname === '/host/properties/add' ||
      pathname === '/host/properties' ||
      pathname.startsWith('/bookings')
    ) {
      return;
    }

    // Don't show if user is already verified or verification is pending
    if (
      user.publicMetadata.isVerified === 'verified' ||
      user.publicMetadata.isVerified === 'pending'
    ) {
      return;
    }

    // Check localStorage for the last closed timestamp
    const lastClosedTime = localStorage.getItem('verificationPopupClosedAt');

    if (lastClosedTime) {
      const elapsedTime = Date.now() - parseInt(lastClosedTime, 10);
      const isExpired = elapsedTime > expirationTimeInMinutes * 60 * 1000;

      if (isExpired) {
        setShowDialog(true);
      }
    } else {
      setShowDialog(true);
    }
  }, [user, pathname]);

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent>
        <DialogHeader onAbort={() => closeDialog()}>
          <DialogTitle>Verify Your Account</DialogTitle>
          <DialogDescription className="text-sm md:text-base">
            Complete verification to unlock all features
          </DialogDescription>
          <div className="py-8">
            <VerficationIntro />
          </div>
        </DialogHeader>
        <DialogFooter className="flex gap-4">
          <DialogClose asChild>
            <Button variant="secondary" onClick={() => closeDialog()}>
              Remind Me Later
            </Button>
          </DialogClose>

          <Link href="/user/verification">
            <Button className="w-full" onClick={() => closeDialog()}>
              Start Verification
            </Button>
          </Link>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VerficationIntro() {
  return (
    <>
      <div className="space-y-2 text-left">
        <h4 className="text-sm font-semibold">Why Verify?</h4>
        <p className="text-sx text-muted-foreground">
          Account verification helps ensure the safety and trust of our community. Verified users
          get priority support and exclusive features.
        </p>
      </div>
      <div className="space-y-2 mt-12 text-left">
        <h4 className="text-sm font-semibold">Simple Three-Step Process</h4>
        <div className="grid gap-4">
          <div className="flex items-center gap-4">
            <Upload className="h-5 w-5 text-primary" />
            <p className="text-sm">Upload a clear photo of your government-issued ID</p>
          </div>
          <div className="flex items-center gap-4">
            <Shield className="h-5 w-5 text-primary" />
            <p className="text-sm">Take a quick selfie for identity confirmation</p>
          </div>
          <div className="flex items-center gap-4">
            <Phone className="h-5 w-5 text-primary" />
            <p className="text-sm">Provide your contact details for account security</p>
          </div>
        </div>
      </div>
    </>
  );
}
